import { useMemo } from "react";

export const DOW = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
export const TYPE_LABEL = { one_time: "One-time", weekly: "Weekly", date_range: "Date Range" };

export const isWeekly = (ev) => ev && ev.event_type === "weekly";
export const weeklyDow = (ev) => ev.recurrence_day || (ev.date ? DOW[new Date(ev.date + "T00:00:00").getDay()] : "");

const dateKey = (d) => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;

const addWeeklyOccurrences = (ev, firstOfMonth, lastOfMonth, add) => {
  const dowIdx = DOW.indexOf(weeklyDow(ev));
  if (dowIdx < 0) return;
  const start = ev.date ? new Date(ev.date + "T00:00:00") : firstOfMonth;
  const stop = ev.end_date ? new Date(ev.end_date + "T00:00:00") : lastOfMonth;
  const cur = new Date(Math.max(start.getTime(), firstOfMonth.getTime()));
  while (cur.getDay() !== dowIdx) cur.setDate(cur.getDate() + 1);
  const until = Math.min(stop.getTime(), lastOfMonth.getTime());
  for (; cur.getTime() <= until; cur.setDate(cur.getDate() + 7)) add(cur, ev);
};

const addDatedOccurrences = (ev, add) => {
  if (!ev.date) return;
  const start = new Date(ev.date + "T00:00:00");
  const end = ev.end_date ? new Date(ev.end_date + "T00:00:00") : start;
  for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) add(d, ev);
};

/** Builds the month grid cells + a date->events map for the given cursor {year, month}. */
export const useEventCalendar = (events, cursor) => {
  const cells = useMemo(() => {
    const startDow = new Date(cursor.year, cursor.month, 1).getDay();
    const daysInMonth = new Date(cursor.year, cursor.month + 1, 0).getDate();
    const out = Array.from({ length: startDow }, (_, i) => ({ key: `pad-start-${i}`, day: null }));
    for (let d = 1; d <= daysInMonth; d++) out.push({ key: `day-${d}`, day: d });
    while (out.length % 7 !== 0) out.push({ key: `pad-end-${out.length}`, day: null });
    return out;
  }, [cursor]);

  const eventsByDate = useMemo(() => {
    const map = {};
    const add = (d, ev) => { (map[dateKey(d)] ||= []).push(ev); };
    const firstOfMonth = new Date(cursor.year, cursor.month, 1);
    const lastOfMonth = new Date(cursor.year, cursor.month + 1, 0);
    events.forEach((ev) => (isWeekly(ev) ? addWeeklyOccurrences(ev, firstOfMonth, lastOfMonth, add) : addDatedOccurrences(ev, add)));
    return map;
  }, [events, cursor]);

  return { cells, eventsByDate };
};

/** Published events still relevant today: weekly (not ended) first in weekday order, then chronological. */
export const useUpcomingEvents = (events) => useMemo(() => {
  const todayIso = new Date().toISOString().slice(0, 10);
  return events
    .filter((e) => (isWeekly(e) ? !e.end_date || e.end_date >= todayIso : (e.end_date || e.date) >= todayIso))
    .sort((a, b) => {
      if (isWeekly(a) !== isWeekly(b)) return isWeekly(a) ? -1 : 1;
      if (isWeekly(a)) return DOW.indexOf(weeklyDow(a)) - DOW.indexOf(weeklyDow(b)) || (a.start_time || "").localeCompare(b.start_time || "");
      return (a.date || "").localeCompare(b.date || "");
    });
}, [events]);
